import { useState, useEffect } from "react";
import "../../../styles/screens/client-visit-screens/CreateNewVisitForm.css";
import Required from "../../common-components/Required";
import BlackButton from "../../custom-components/BlackButton";
import CancelButton from "../../custom-components/CancelButton";
import CloseButton from "../../custom-components/CloseButton";
import { ClientVisitScreenStringLabels } from "../../../util/Strings";
import { NewVisitFormValidations } from "../../../util/Validations";
import { GetDate } from "../../../util/FilterDate";
import ErrorMessageComponent from "../../common-components/ErrorMessageComponent";
import { useDispatch, useSelector } from "react-redux";
import useFileValidation from "../../../util/useFileValidation";
import {
  fileDelete,
  setSelectedFile,
  previewImage,
  progressPercentage,
  fileUpload,
} from "../../../slices/FileUploadSlice";
import {
  setPreThumbnailId,
  setPrevThumbnailDetails,
} from "../../../slices/VisitSlice";
import ErrorAlert from "../../common-components/ErrorAlert";
import ErrorMessages from "../../../util/ErrorMessages";
import FileUploader from "../../common-components/FileUploader";
import ImagePreviewWithFilename from "../../common-components/ImagePreviewWithFilename";

import SupportTeamList from "./SupportTeamList";
import AddSupportTeamForm from "./AddSupportTeamForm";

function CreateNewVisitForm({ show, onClickNext, data }) {
  const dispatch = useDispatch();

  // Main form state including company fields and support team array
  const [state, setState] = useState({
    name: data?.name || "",
    startDate: data?.startDate || "",
    endDate: data?.endDate || "",
    region: data?.region || "Bangalore AC",
    companyName: data?.companyName || "",
    companyLogoId: data?.companyLogoId || null,
    isInternalVisit: data?.isInternalVisit || false,
    supportTeamMembers: data?.supportTeamMembers || [],
  });

  const [prevFileData, setPreFileData] = useState({
    photoUrl: data?.companyLogoUrl || null,
    fileName: data?.fileName || null,
  });

  // Support team array and modal controls (edit by index)
  const [supportTeamList, setSupportTeamList] = useState(
    data?.supportTeamMembers || []
  );
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  // Other states
  const [prevFileId, setPrevFileId] = useState(data?.companyLogoId || null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [validation, setValidation] = useState({});
  const { errorSingleMessage, setErrorSingleMessage, validateFile } =
    useFileValidation();
  const [imageDeleteList, setImageDeleteList] = useState([]);

  const resetPrevFileData = useSelector(
    (state) => state.visits.prevThumbnailDetails
  );
  const errorMessageCode = useSelector((state) => state.upload.errorCode);

  function handleChange(e) {
    const value = e.target.value;
    setState({ ...state, [e.target.name]: value });
  }

  // Image cancel handler for company logo (unrelated to support team photos)
  function handleImageCancel() {
    setState({ ...state, companyLogoId: null });
    setPreFileData({ photoUrl: null, fileName: null });
    setImageDeleteList([...imageDeleteList, prevFileId]);
  }

  function handleCloseErrorAlert() {
    setPreFileData(resetPrevFileData);
    handleState();
    dispatch(setSelectedFile(null));
    setImageDeleteList([]);
    onClickNext();
  }

  function resetImage() {
    dispatch(progressPercentage(0));
    dispatch(setSelectedFile(null));
    dispatch(previewImage(null));
  }

  function handleState() {
    setState({ ...state, companyLogoId: data?.companyLogoId });
  }

  const checkValidation = () => {
    const { errors, isValid } = NewVisitFormValidations(state);
    setValidation(errors);
    return isValid;
  };

  // Open add support team form for new member
  const handleAddSupportTeamClick = () => {
    setEditIndex(null);
    setIsAddEditOpen(true);
  };

  // Edit existing support team member by index
  const handleEditSupportTeam = (index) => {
    if (index >= 0 && index < supportTeamList.length) {
      setEditIndex(index);
      setIsAddEditOpen(true);
    }
  };

  // Delete support team member by index
  const handleDeleteSupportTeam = (index) => {
    if (index >= 0 && index < supportTeamList.length) {
      setSupportTeamList((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Save support team member (add or update)
  const handleSupportTeamSave = (supportTeamMember) => {
    if (!supportTeamMember) {
      setIsAddEditOpen(false);
      setEditIndex(null);
      return;
    }
    setSupportTeamList((prev) => {
      if (editIndex !== null && editIndex >= 0 && editIndex < prev.length) {
        const updated = [...prev];
        updated[editIndex] = supportTeamMember;
        return updated;
      } else {
        return [...prev, supportTeamMember];
      }
    });
    setIsAddEditOpen(false);
    setEditIndex(null);
  };

  // Update main form `state` when support team list changes
  useEffect(() => {
    setState((prev) => ({ ...prev, supportTeamMembers: supportTeamList }));
  }, [supportTeamList]);

  function handleNextButton() {
    if (checkValidation()) {
      if (state.name && state.startDate && state.endDate && state.region) {
        resetImage();

        if (data) {
          imageDeleteList.forEach((image) => {
            dispatch(fileDelete({ id: image }));
          });

          setPhotoUrl(null);

          onClickNext(state);

          setPreFileData({
            photoUrl: data?.companyLogoUrl || null,
            fileName: data?.fileName || null,
          });
          setErrorSingleMessage("");
          setImageDeleteList([]);
        } else {
          setState({});
          setPhotoUrl(null);
          setImageDeleteList([]);
          onClickNext(state);
        }
      } else {
        onClickNext();
      }
    }
  }

  const handleFileSelect = (event) => {
    const files = event.target.files;
    postFileData(files);
  };

  const handleDrag = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postFileData(files);
  };

  function postFileData(files) {
    const valid = validateFile(files);
    if (valid) {
      const file = files[0];

      if (prevFileData) {
        dispatch(setPreThumbnailId(prevFileId));
        setPrevFileId(prevFileId);
        dispatch(setPrevThumbnailDetails(prevFileData));
        setPreFileData({ photoUrl: null, fileName: null });
      }
      if (file) {
        dispatch(setSelectedFile(file));
        dispatch(fileUpload(file))
          .unwrap()
          .then((res) => {
            setPhotoUrl(res[0]._id);
            setState((prev) => ({ ...prev, companyLogoId: res[0]._id }));
            setPrevFileId(res[0]._id);
          });
      }
    }
  }

  if (!show) return null;

  if (errorMessageCode) {
    return (
      <ErrorAlert
        show={errorMessageCode}
        onClickHandle={handleCloseErrorAlert}
        title="Error"
        subTitle={ErrorMessages(errorMessageCode)}
      />
    );
  }

  return (
    <div className="modal">
      <div className="form-scroll">
        <div className="form-flex">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <div className="header">{data ? "Update Visit" : "Create New Visit"}</div>
              <CloseButton
                onClickhandle={() => {
                  if (!data) {
                    setState({});
                    setValidation({});
                    setSupportTeamList([]);
                  }
                  setImageDeleteList([]);
                  onClickNext();
                }}
              />
            </div>
            <div className="form-container">
              {/* NAME */}
              <div className="form-label-input">
                <label className="form-label">
                  {ClientVisitScreenStringLabels.name}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    value={state.name}
                    name="name"
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Input"
                    required
                  />
                </div>
                <ErrorMessageComponent message={validation.name} />
              </div>
              {/* START DATE */}
              <div className="form-label-input">
                <label className="form-label">
                  {ClientVisitScreenStringLabels.startDate}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="date"
                    name="startDate"
                    value={state.startDate}
                    onChange={handleChange}
                    placeholder="Input"
                    required
                    min={GetDate()}
                  />
                </div>
                <ErrorMessageComponent message={validation.startDate} />
              </div>
              {/* END DATE */}
              <div className="form-label-input">
                <label className="form-label">
                  {ClientVisitScreenStringLabels.endDate}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="date"
                    name="endDate"
                    value={state.endDate}
                    onChange={handleChange}
                    placeholder="Input"
                    required
                    min={GetDate()}
                  />
                </div>
                <ErrorMessageComponent message={validation.endDate} />
              </div>
              {/* REGION */}
              <div className="form-label-input">
                <label className="form-label">
                  {ClientVisitScreenStringLabels.region}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="region"
                    value={state.region}
                    onChange={handleChange}
                    placeholder="Input"
                    required
                  />
                </div>
                <ErrorMessageComponent message={validation.region} />
              </div>
              {/* COMPANY NAME */}
              <div className="form-label-input">
                <label className="form-label">{ClientVisitScreenStringLabels.companyName}</label>
                <div className="form-input-container">
                  <input
                    type="text"
                    value={state.companyName}
                    name="companyName"
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Input"
                  />
                </div>
                <ErrorMessageComponent message={validation.companyName} />
              </div>
              {/* SUPPORT TEAM SECTION, only show if Internal Visit is checked */}
              {state.isInternalVisit && (
                <div className="form-label-input support-team-section">
                  <label className="form-label">Support Team</label>
                  <button
                    type="button"
                    className="add-support-team-button"
                    onClick={handleAddSupportTeamClick}
                    style={{ cursor: "pointer", marginBottom: "0.5rem" }}
                  >
                    +Add Member
                  </button>
                  <SupportTeamList
                    supportTeamArray={supportTeamList}
                    onEdit={handleEditSupportTeam}
                    onDelete={handleDeleteSupportTeam}
                  />
                </div>
              )}

              {/* FILE UPLOADER */}
              <FileUploader
                previewShape="circular"
                headerName="Company Logo Image"
                handleDragOver={handleDrag}
                handleDrop={handleDrop}
                handleFileSelect={handleFileSelect}
                setPreFileData={setPreFileData}
                handleState={handleState}
                type="visit"
              />

              {errorSingleMessage && (
                <p className="error image-error">{errorSingleMessage}</p>
              )}

              {prevFileData?.photoUrl && data?.companyLogoUrl && data?.fileName && (
                <ImagePreviewWithFilename
                  photoUrl={data.companyLogoUrl}
                  fileName={data.fileName}
                  previewShape="circular"
                  handleCancel={handleImageCancel}
                />
              )}

              {/* VISIT CHECKBOX */}
              <div className="form-label-input-checkbox" style={{ margin: 0 }}>
                <div className="checkbox">
                  <input
                    className="checkbox-input"
                    type="checkbox"
                    checked={state.isInternalVisit}
                    name="isInternalVisit"
                    disabled={!!data}
                    onChange={(e) =>
                      setState({ ...state, isInternalVisit: e.target.checked })
                    }
                  />
                </div>
                <label className="form-label" style={{ marginBottom: 0 }}>
                  {ClientVisitScreenStringLabels.visitCheckBoxLabel}
                </label>
              </div>
              {/* FOOTER BUTTONS */}
              <div className="footer-button-container">
                <CancelButton
                  onClickhandle={() => {
                    if (!data) {
                      setState({});
                      setValidation({});
                      setSupportTeamList([]);
                    }
                    setImageDeleteList([]);
                    onClickNext();
                  }}
                >
                  {ClientVisitScreenStringLabels.cancel}
                </CancelButton>
                <BlackButton onClickhandle={handleNextButton}>
                  {data ? ClientVisitScreenStringLabels.update : ClientVisitScreenStringLabels.create}
                </BlackButton>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ADD / EDIT SUPPORT TEAM FORM MODAL */}
      {isAddEditOpen && (
        <AddSupportTeamForm
          show={true}
          data={editIndex !== null ? supportTeamList[editIndex] : null}
          onClickNext={handleSupportTeamSave}
          onClose={() => {
            setIsAddEditOpen(false);
            setEditIndex(null);
          }}
        />
      )}
    </div>
  );
}

export default CreateNewVisitForm;